import _ from 'lodash'
import { date } from 'quasar'
import { imgixHost } from 'src/config'
import MembershipDiscount, { MEMBERSHIP_DISCOUNT_CONDITIONS } from 'src/models/membership-type/MembershipDiscount'
import MembershipPoint, { CONDITIONS } from 'src/models/membership-type/MembershipPoint'
import MembershipTier from 'src/models/membership-type/MembershipTier'
import MembershipUserType from 'src/models/membership-type/MembershipUserType'
import MembershipVoucher, { MEMBERSHIP_VOUCHER_CONDITIONS } from 'src/models/membership-type/MembershipVoucher'
import Model, { ID, Integer, MODEL_LEVEL } from 'src/models/model'
import Site from 'src/models/site/Site'

export const MEMBERSHIP_TYPE_DESCRIPTION_TYPES = {
  PREFIX: 'prefix',
  REPLACE: 'replace'
}
MEMBERSHIP_TYPE_DESCRIPTION_TYPES.ALL = _.values(MEMBERSHIP_TYPE_DESCRIPTION_TYPES)

export const MEMBERSHIP_TIER_CONDITIONS_TERMS = {
  MONTHS: 'months',
  YEARS: 'years'
}
MEMBERSHIP_TIER_CONDITIONS_TERMS.ALL = _.values(MEMBERSHIP_TIER_CONDITIONS_TERMS)

export const MEMBERSHIP_BENEFIT_RENEWS = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  BIANNUALLY: 'biannually',
  ANNUALLY: 'annually'
}
MEMBERSHIP_BENEFIT_RENEWS.ALL = _.values(MEMBERSHIP_BENEFIT_RENEWS)
MEMBERSHIP_BENEFIT_RENEWS.FOR_VOUCHERS = [MEMBERSHIP_BENEFIT_RENEWS.MONTHLY, MEMBERSHIP_BENEFIT_RENEWS.ANNUALLY]

export const MEMBERSHIP_BENEFIT_CONDITIONS = {
  BIRTHDAY: 'birthday',
  BIRTHWEEK: 'birthweek',
  CUSTOM_DATES: 'custom-dates'
}
MEMBERSHIP_BENEFIT_CONDITIONS.ALL = _.values(MEMBERSHIP_BENEFIT_CONDITIONS)
MEMBERSHIP_BENEFIT_CONDITIONS.FOR_DISCOUNTS = [MEMBERSHIP_BENEFIT_CONDITIONS.BIRTHDAY, MEMBERSHIP_BENEFIT_CONDITIONS.BIRTHWEEK, MEMBERSHIP_BENEFIT_CONDITIONS.CUSTOM_DATES]
MEMBERSHIP_BENEFIT_CONDITIONS.FOR_VOUCHERS = [MEMBERSHIP_BENEFIT_CONDITIONS.BIRTHDAY, MEMBERSHIP_BENEFIT_CONDITIONS.CUSTOM_DATES]

export const MEMBERSHIP_TERMS = {
  MONTHLY: {
    label: 'Monthly',
    value: 1
  },
  QUARTERLY: {
    label: 'Quarterly',
    value: 3
  },
  BIANNUAL: {
    label: 'Biannual',
    value: 6
  },
  ANNUAL: {
    label: 'Annual',
    value: 12
  }
}
MEMBERSHIP_TERMS.ALL = _.values(MEMBERSHIP_TERMS)

export default class MembershipType extends Model {
  static name = 'MembershipType'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.CIRCUIT

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      name: {
        type: String,
        sortable: true,
        translatable: true
      },
      descriptionType: {
        type: String,
        default: MEMBERSHIP_TYPE_DESCRIPTION_TYPES.PREFIX
      },
      description: {
        type: String,
        default: ''
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      default: {
        type: Boolean,
        default: false
      },
      free: {
        type: Boolean,
        default: false
      },
      hide: {
        type: Boolean,
        default: false
      },
      ageVerificationRequired: {
        type: Boolean,
        default: false
      },
      availableOnline: {
        type: Boolean,
        default: false
      },
      previewOnline: {
        type: Boolean,
        default: false
      },
      allowRenewalOnline: {
        type: Boolean,
        default: true
      },
      conditionsTerm: {
        type: String,
        default: MEMBERSHIP_TIER_CONDITIONS_TERMS.MONTHS,
        required: false
      },
      overrideCircuitTermOptions: {
        type: Boolean,
        default: false
      },
      allowMonthlyMembership: {
        type: Boolean,
        default: true
      },
      allowQuarterlyMembership: {
        type: Boolean,
        default: false
      },
      quarterlyDiscountPercentThousandths: {
        type: Integer,
        default: 5 * 1000
      },
      allowBiannualMembership: {
        type: Boolean,
        default: false
      },
      biannualDiscountPercentThousandths: {
        type: Integer,
        default: 10 * 1000
      },
      allowAnnualMembership: {
        type: Boolean,
        default: false
      },
      annualDiscountPercentThousandths: {
        type: Integer,
        default: 20 * 1000
      },
      sites: {
        type: [Site],
        reference: true,
        default: () => ([])
      },
      excludeSites: {
        type: Boolean,
        default: true
      },
      membershipUserTypes: {
        type: [MembershipUserType],
        allowDestroy: true
      },
      membershipTiers: {
        type: [MembershipTier],
        allowDestroy: true
      },
      membershipPoints: {
        type: [MembershipPoint],
        allowDestroy: true
      },
      membershipVouchers: {
        type: [MembershipVoucher],
        allowDestroy: true
      },
      membershipDiscounts: {
        type: [MembershipDiscount],
        allowDestroy: true
      },
      iconImage: {
        type: String,
        default: null
      },
      iconImageParams: {
        type: String,
        default: 'w=128&h=128&fit=fill&fill=solid&auto=format',
        local: true
      },
      iconImagePreviewParams: {
        type: String,
        default: 'w=128&h=128&fit=fill&fill=solid&transparency=grid&auto=format',
        local: true
      }
    }

    this.computed = {
      hasTiers () {
        return !!this.membershipTiers?.data?.length
      },
      tiersReversed () {
        return _.reverse(_.clone(this.membershipTiers?.data))
      },
      activeMembershipVouchers () {
        return _.filter(this.membershipVouchers?.data, 'enable')
      },
      conditionalMembershipVouchers () {
        return _.filter(this.activeMembershipVouchers, 'isConditional')
      },
      nonTierConditionalMembershipVouchers () {
        return _.filter(this.conditionalMembershipVouchers, voucher =>
          (voucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER] === false || _.isNil(voucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER])) &&
          (voucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER_UPGRADE] === false || _.isNil(voucher.conditions[MEMBERSHIP_VOUCHER_CONDITIONS.TIER_UPGRADE])))
      },
      unconditionalMembershipVouchers () {
        return _.filter(this.activeMembershipVouchers, { isConditional: false })
      },
      unlimitedVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, { limitRedemptions: false })
      },
      unlimitedTicketVouchers () {
        return _.filter(this.unlimitedVouchers, 'isTicket')
      },
      hasUnlimitedTicketVouchers () {
        return !!this.unlimitedTicketVouchers.length
      },
      annualVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, 'isAnnual')
      },
      annualTicketVouchers () {
        return _.filter(this.annualVouchers, 'isTicket')
      },
      hasAnnualTicketVouchers () {
        return !!this.annualTicketVouchers.length
      },
      annualTicketVouchersCount () {
        return _.sumBy(this.annualTicketVouchers, 'redemptionsLimit')
      },
      monthlyVouchers () {
        return _.filter(this.unconditionalMembershipVouchers, 'isMonthly')
      },
      monthlyTicketVouchers () {
        return _.filter(this.monthlyVouchers, 'isTicket')
      },
      hasMonthlyTicketVouchers () {
        return !!this.monthlyTicketVouchers.length
      },
      monthlyTicketVouchersCount () {
        return _.sumBy(this.monthlyTicketVouchers, 'redemptionsLimit')
      },
      activeMembershipDiscounts () {
        return _.filter(this.membershipDiscounts?.data, 'enable')
      },
      conditionalMembershipDiscounts () {
        return _.filter(this.activeMembershipDiscounts, 'isConditional')
      },
      nonTierConditionalMembershipDiscounts () {
        return _.filter(this.conditionalMembershipDiscounts, discount => (discount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.TIER] === false || _.isNil(discount.conditions[MEMBERSHIP_DISCOUNT_CONDITIONS.TIER])))
      },
      unconditionalMembershipDiscounts () {
        return _.filter(this.activeMembershipDiscounts, { isConditional: false })
      },
      unlimitedDiscounts () {
        return _.filter(this.unconditionalMembershipDiscounts, { limitRedemptions: false })
      },
      hasUnlimitedDiscounts () {
        return !!this.unlimitedDiscounts.length
      },
      limitedDiscounts () {
        return _.filter(this.unconditionalMembershipDiscounts, 'limitRedemptions')
      },
      hasLimitedDiscounts () {
        return !!this.limitedDiscounts.length
      },
      activeMembershipPoints () {
        return _.filter(this.membershipPoints?.data, 'isActive')
      },
      hasMembershipPoints () {
        return this.activeMembershipPoints.length > 0
      },
      conditionalPoints () {
        return _.filter(this.activeMembershipPoints, 'isConditional')
      },
      nonTierConditionalPoints () {
        return _.filter(this.conditionalPoints, point => (point.conditions[CONDITIONS.TIER] === false || _.isNil(point.conditions[CONDITIONS.TIER])))
      },
      unconditionalPoints () {
        return _.filter(this.activeMembershipPoints, { isConditional: false })
      },
      hasUnconditionalPoints () {
        return !!this.unconditionalPoints.length
      },
      unconditionalVisitPoints () {
        return _.sumBy(this.unconditionalPoints, 'visitPointsAccumulationRate')
      },
      unconditionalConcessionsPoints () {
        return _.sumBy(this.unconditionalPoints, 'concessionPointsAccumulationRate')
      },
      unconditionalTicketPricePoints () {
        return _.sumBy(this.unconditionalPoints, 'ticketPricePointsAccumulationRate')
      },
      unconditionalTicketValuePoints () {
        return _.sumBy(this.unconditionalPoints, 'ticketValuePointsAccumulationRate')
      },
      iconImageUrl () {
        if (!this.iconImage) return ''

        return `${imgixHost}/${this.iconImage}?${this.iconImageParams}`
      },
      iconImagePreviewUrl () {
        if (!this.iconImage) return ''

        return `${imgixHost}/${this.iconImage}?${this.iconImagePreviewParams}`
      }
    }
  }

  getDiscountPercent (term) {
    switch (term) {
      case MEMBERSHIP_TERMS.MONTHLY.value:
        return 0
      case MEMBERSHIP_TERMS.QUARTERLY.value:
        return this.allowQuarterlyMembership ? this.quarterlyDiscountPercentThousandths / 1000 : 0
      case MEMBERSHIP_TERMS.BIANNUAL.value:
        return this.allowBiannualMembership ? this.biannualDiscountPercentThousandths / 1000 : 0
      case MEMBERSHIP_TERMS.ANNUAL.value:
        return this.allowAnnualMembership ? this.annualDiscountPercentThousandths / 1000 : 0
      default:
        throw new Error('Unhandled term length: ' + term)
    }
  }

  hasUserClassForUser (user) {
    return (this.free && !this.membershipUserTypes.data.length) || !!this.membershipUserTypeForUser(user)
  }

  membershipUserTypeForUser (user) {
    if (!user || !this.membershipUserTypes) return null

    return _.find(this.membershipUserTypes.data, membershipUserType => membershipUserType.userClassId === user.userClassId)
  }

  availableMembershipUserTypesForUser (user) {
    let ageToday = null

    if (user?.dateOfBirth) {
      const birthdate = date.extractDate(user.dateOfBirth, 'YYYY-MM-DD')
      const today = new Date()
      ageToday = Math.floor(date.getDateDiff(today, birthdate, 'days') / 365)
    }

    if (!ageToday && this.ageVerificationRequired) {
      return []
    } else if (!ageToday) {
      return this.membershipUserTypes.data
    } else {
      return _.filter(this.membershipUserTypes.data, membershipUserType => {
        return (
          !membershipUserType.userClass.internal || user?.employee
        ) && (
          !membershipUserType.userClass.minAge || membershipUserType.userClass.minAge <= ageToday
        ) && (
          !membershipUserType.userClass.maxAge || membershipUserType.userClass.maxAge >= ageToday
        )
      })
    }
  }

  membershipUserTypeForUserClass (userClass) {
    if (!userClass || !this.membershipUserTypes) return null

    return _.find(this.membershipUserTypes.data, membershipUserType => membershipUserType.userClassId === userClass.id)
  }
}
